/* eslint-disable-next-line */
import { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { TestableComponentProps } from '../types/ui-kit.types';
export interface IconProps extends TestableComponentProps {
    name: string;
    size?: number;
    color?: string;
    className?: string;
    tooltip?: string;
    'data-testid'?: string;
    'data-test-id'?: string;
    tooltipPosition?: 'top' | 'bottom' | 'left' | 'right';
    tooltipStyle?: React.CSSProperties;
}

export function Icon(props: IconProps) {
    const [showTooltip, setShowTooltip] = useState(false);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const iconRef = useRef<HTMLDivElement>(null);

    const tooltipStyle = {
        top: tooltipPosition.top,
        left: tooltipPosition.left,
        backgroundColor: props.tooltipStyle?.backgroundColor ?? '#263238', // QM Neutral grey 900
        borderRadius: props.tooltipStyle?.borderRadius ?? '4px',
        padding: props.tooltipStyle?.padding ?? '4px 8px',
        fontSize: props.tooltipStyle?.fontSize ?? '12px',
        lineHeight: props.tooltipStyle?.lineHeight ?? '16px',
        whiteSpace: props.tooltipStyle?.whiteSpace ?? 'nowrap',
        width: props.tooltipStyle?.width ?? 'hug-content',
        height: props.tooltipStyle?.height ?? 'hug-content',
        ...props.tooltipStyle,
    };
    const classes = twMerge(`${props.name} text-${props.color} ${props.className}`);

    const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
        if (props.tooltip) {
            const rect = event.currentTarget.getBoundingClientRect();
            let top = rect.bottom + window.scrollY + 6;
            let left = rect.left + window.scrollX + rect.width / 2;
            if (props.tooltipPosition === 'top') {
                top = rect.top + window.scrollY - 6;
            } else if (props.tooltipPosition === 'left') {
                left = rect.left + window.scrollX - 6;
            } else if (props.tooltipPosition === 'right') {
                left = rect.right + window.scrollX + 6;
            }
            setTooltipPosition({ top, left });
            setShowTooltip(true);
        }
    };

    const handleMouseLeave = () => {
        setShowTooltip(false);
    };

    const tooltip =
        props.tooltip &&
        showTooltip &&
        createPortal(
            <div
                className="fixed z-50 px-2 py-1 text-xs text-white rounded pointer-events-none transform -translate-x-1/2"
                style={tooltipStyle}
            >
                {props.tooltip}
                <div
                    style={{
                        position: 'absolute',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        ...(props.tooltipPosition === 'top'
                            ? {
                                  bottom: '-6px',
                                  borderWidth: '6px 6px 0 6px',
                                  borderColor: '#263238 transparent transparent transparent',
                                  borderStyle: 'solid',
                              }
                            : props.tooltipPosition === 'bottom'
                            ? {
                                  top: '-6px',
                                  borderWidth: '0 6px 6px 6px',
                                  borderColor: 'transparent transparent #263238 transparent',
                                  borderStyle: 'solid',
                              }
                            : {}),
                    }}
                />
            </div>,
            document.body,
        );

    return (
        <>
            <div
                ref={iconRef}
                data-testid={props['data-testid'] || `icon-${props.name}`}
                data-test-id={props['data-test-id'] || `icon-${props.name}`}
                className={classes}
                style={{ fontSize: props.size }}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            ></div>
            {tooltip}
        </>
    );
}

Icon.defaultProps = {
    name: 'ri-eye-line',
    color: 'black',
    size: 36,
};

export default Icon;
